import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle, XCircle, Key, Mail, Shield, Calendar, Award, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LicenseTestPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    license_key: "",
    user_email: ""
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleTest = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setResult(null);
      
      const response = await axios.post(`${API}/licenses/verify`, formData);
      
      setResult(response.data);
      
      if (response.data.valid) {
        toast.success("License is valid!");
      } else {
        toast.error("License is invalid");
      }
    } catch (error) {
      toast.error("Failed to verify license");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code, language) => {
    navigator.clipboard.writeText(code);
    toast.success(`${language} code copied to clipboard!`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-500 to-blue-700 bg-clip-text text-transparent mb-2">
              License API Test Page
            </h1>
            <p className="text-gray-600 text-lg">Test your license validation integration</p>
          </div>
          <button
            data-testid="back-to-home-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:border-blue-500 hover:text-blue-700 transition-colors text-sm font-medium text-gray-700 rounded shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Test Form */}
          <Card className="glass border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Key className="w-6 h-6 text-blue-700" />
                Test License Validation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTest} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-700" />
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.user_email}
                    onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                    placeholder="user@example.com"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="license" className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-blue-700" />
                    License Key
                  </Label>
                  <Input
                    id="license"
                    type="text"
                    value={formData.license_key}
                    onChange={(e) => setFormData({ ...formData, license_key: e.target.value })}
                    placeholder="Enter license key"
                    required
                    className="mt-1 font-mono"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white"
                >
                  {loading ? "Validating..." : "Validate License"}
                </Button>
              </form>

              {/* Result Display */}
              {result && (
                <div className={`mt-6 p-6 rounded-lg border-2 ${result.valid ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    {result.valid ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    ) : (
                      <XCircle className="w-8 h-8 text-red-600" />
                    )}
                    <div>
                      <h3 className={`font-bold text-lg ${result.valid ? 'text-green-800' : 'text-red-800'}`}>
                        {result.valid ? 'Valid License' : 'Invalid License'}
                      </h3>
                      <p className={`text-sm ${result.valid ? 'text-green-600' : 'text-red-600'}`}>
                        {result.message}
                      </p>
                    </div>
                  </div>

                  {result.valid && result.license_data && (
                    <div className="space-y-3 border-t border-green-200 pt-4">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-green-600" />
                        <span className="font-semibold">Tier:</span>
                        <span className="px-2 py-1 bg-green-600 text-white rounded-full text-xs uppercase">
                          {result.license_data.tier}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-green-600" />
                        <span className="font-semibold">Status:</span>
                        <span className={`px-2 py-1 rounded-full text-xs uppercase ${
                          result.license_data.status === 'active' ? 'bg-green-600 text-white' : 'bg-gray-600 text-white'
                        }`}>
                          {result.license_data.status}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-green-600" />
                        <span className="font-semibold">Days Remaining:</span>
                        <span className="text-green-700">{result.license_data.days_remaining} days</span>
                      </div>

                      <div>
                        <p className="font-semibold mb-2">Features:</p>
                        <ul className="space-y-1">
                          {result.license_data.features.map((feature, idx) => (
                            <li key={feature || `f-${idx}`} className="flex items-center gap-2 text-sm text-green-700">
                              <CheckCircle className="w-3 h-3" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* API Info */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">API Endpoint</h4>
                <code className="text-xs bg-white px-2 py-1 rounded border border-blue-200 break-all block">
                  POST {API}/licenses/verify
                </code>
              </div>
            </CardContent>
          </Card>

          {/* Integration Examples */}
          <div className="space-y-6">
            {/* JavaScript Example */}
            <Card className="glass border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-lg">JavaScript Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto">
{`const API_URL = '${API}/licenses/verify';

async function validateLicense(key, email) {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      license_key: key,
      user_email: email
    })
  });
  
  return await response.json();
}

// Usage
const result = await validateLicense(
  'license-key-here',
  'user@example.com'
);

if (result.valid) {
  console.log('License valid!');
  console.log('Tier:', result.license_data.tier);
} else {
  console.log('Invalid license');
}`}
                </pre>
                <Button
                  onClick={() => copyCode(`const API_URL = '${API}/licenses/verify';\n\nasync function validateLicense(key, email) {\n  const response = await fetch(API_URL, {\n    method: 'POST',\n    headers: { 'Content-Type': 'application/json' },\n    body: JSON.stringify({ license_key: key, user_email: email })\n  });\n  return await response.json();\n}`, 'JavaScript')}
                  variant="outline"
                  className="w-full mt-2"
                  size="sm"
                >
                  Copy Code
                </Button>
              </CardContent>
            </Card>

            {/* Python Example */}
            <Card className="glass border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-lg">Python Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto">
{`import requests

API_URL = "${API}/licenses/verify"

def validate_license(key, email):
    response = requests.post(
        API_URL,
        json={
            "license_key": key,
            "user_email": email
        }
    )
    return response.json()

# Usage
result = validate_license(
    "license-key-here",
    "user@example.com"
)

if result["valid"]:
    print("License valid!")
    print(f"Tier: {result['license_data']['tier']}")
else:
    print("Invalid license")`}
                </pre>
                <Button
                  onClick={() => copyCode(`import requests\n\nAPI_URL = "${API}/licenses/verify"\n\ndef validate_license(key, email):\n    response = requests.post(API_URL, json={"license_key": key, "user_email": email})\n    return response.json()`, 'Python')}
                  variant="outline"
                  className="w-full mt-2"
                  size="sm"
                >
                  Copy Code
                </Button>
              </CardContent>
            </Card>

            {/* C# Example */}
            <Card className="glass border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-lg">C# Integration</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto">
{`using System.Net.Http;
using System.Text.Json;

var API_URL = "${API}/licenses/verify";

async Task<LicenseResponse> ValidateLicense(
    string key, string email)
{
    using var client = new HttpClient();
    var data = new { 
        license_key = key, 
        user_email = email 
    };
    var json = JsonSerializer.Serialize(data);
    var content = new StringContent(json);
    
    var response = await client.PostAsync(
        API_URL, content
    );
    var body = await response.Content
        .ReadAsStringAsync();
    
    return JsonSerializer.Deserialize
        <LicenseResponse>(body);
}`}
                </pre>
                <Button
                  onClick={() => copyCode(`using System.Net.Http;\nusing System.Text.Json;\n\nvar API_URL = "${API}/licenses/verify";\n\nasync Task<LicenseResponse> ValidateLicense(string key, string email) {\n    using var client = new HttpClient();\n    var data = new { license_key = key, user_email = email };\n    var json = JsonSerializer.Serialize(data);\n    var content = new StringContent(json);\n    var response = await client.PostAsync(API_URL, content);\n    return JsonSerializer.Deserialize<LicenseResponse>(await response.Content.ReadAsStringAsync());\n}`, 'C#')}
                  variant="outline"
                  className="w-full mt-2"
                  size="sm"
                >
                  Copy Code
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseTestPage;
